import { useEffect, useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useToast } from '@/hooks/use-toast';
import { Loader2, KeyRound, Send, History } from 'lucide-react';

type Cred = { id: string; credential_key: string; credential_value: string; is_active: boolean };
type LogRow = { id: string; action: string; status: string; error_message: string | null; created_at: string; request_summary: any; response_summary: any };

const TWILIO_KEYS: { key: string; label: string; placeholder: string; type?: string }[] = [
  { key: 'account_sid', label: 'Account SID', placeholder: 'ACxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx' },
  { key: 'auth_token', label: 'Auth Token', placeholder: 'Your Twilio auth token', type: 'password' },
  { key: 'from_number', label: 'From Number', placeholder: '+15017122661' },
];

export default function IntegrationsManager() {
  const { toast } = useToast();
  const [creds, setCreds] = useState<Record<string, Cred>>({});
  const [draft, setDraft] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [logs, setLogs] = useState<LogRow[]>([]);
  const [testTo, setTestTo] = useState('');
  const [testMsg, setTestMsg] = useState('Test message from Lovable.');
  const [sending, setSending] = useState(false);

  const load = async () => {
    setLoading(true);
    const [c, l] = await Promise.all([
      supabase.from('integration_credentials').select('*').eq('provider', 'twilio'),
      supabase.from('integration_logs').select('*').eq('provider', 'twilio').order('created_at', { ascending: false }).limit(25),
    ]);
    const map: Record<string, Cred> = {};
    (c.data || []).forEach((r: any) => { map[r.credential_key] = r; });
    setCreds(map);
    setLogs((l.data as any) || []);
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  const save = async () => {
    setSaving(true);
    const rows = Object.entries(draft)
      .filter(([, v]) => v && v.trim())
      .map(([credential_key, credential_value]) => ({
        provider: 'twilio',
        credential_key,
        credential_value: credential_value.trim(),
        is_active: true,
      }));

    if (rows.length === 0) { setSaving(false); return; }

    const { error } = await supabase
      .from('integration_credentials')
      .upsert(rows, { onConflict: 'provider,credential_key' });

    setSaving(false);
    if (error) {
      toast({ title: 'Save failed', description: error.message, variant: 'destructive' });
      return;
    }
    toast({ title: 'Credentials saved' });
    setDraft({});
    load();
  };

  const sendTest = async () => {
    if (!testTo.startsWith('+')) {
      toast({ title: 'Invalid number', description: 'Use E.164 format like +15558675310', variant: 'destructive' });
      return;
    }
    setSending(true);
    const { data, error } = await supabase.functions.invoke('twilio-send-sms', {
      body: { to: testTo, message: testMsg },
    });
    setSending(false);
    if (error || (data as any)?.error) {
      toast({ title: 'Send failed', description: (data as any)?.error || error?.message, variant: 'destructive' });
    } else {
      toast({ title: 'SMS queued', description: `SID: ${(data as any).sid}` });
    }
    load();
  };

  const mask = (v?: string) => (v ? `${v.slice(0, 4)}••••${v.slice(-3)}` : '—');

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold">Third-Party Integrations</h1>
        <p className="text-muted-foreground text-sm">Manage API credentials for external services. Stored securely; only SuperAdmins can view.</p>
      </div>

      <Tabs defaultValue="twilio">
        <TabsList>
          <TabsTrigger value="twilio">
            <KeyRound className="h-4 w-4 mr-1.5" /> Twilio (SMS)
          </TabsTrigger>
        </TabsList>

        <TabsContent value="twilio" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                Twilio Credentials
                {Object.keys(creds).length === TWILIO_KEYS.length
                  ? <Badge variant="default">Configured</Badge>
                  : <Badge variant="secondary">Incomplete</Badge>}
              </CardTitle>
              <CardDescription>
                Enter the API credentials from your Twilio Console. Leave a field blank to keep the existing value.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {loading ? (
                <div className="flex items-center text-muted-foreground"><Loader2 className="h-4 w-4 animate-spin mr-2" />Loading…</div>
              ) : (
                <>
                  {TWILIO_KEYS.map(({ key, label, placeholder, type }) => (
                    <div key={key} className="space-y-1.5">
                      <Label htmlFor={key}>{label}</Label>
                      <Input
                        id={key}
                        type={type || 'text'}
                        placeholder={creds[key] ? `Current: ${mask(creds[key].credential_value)}` : placeholder}
                        value={draft[key] || ''}
                        onChange={(e) => setDraft((d) => ({ ...d, [key]: e.target.value }))}
                      />
                    </div>
                  ))}
                  <Button onClick={save} disabled={saving || Object.keys(draft).length === 0}>
                    {saving && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
                    Save Credentials
                  </Button>
                </>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2"><Send className="h-5 w-5" />Send Test SMS</CardTitle>
              <CardDescription>Verify your Twilio integration end-to-end via the API gateway.</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-1.5">
                <Label htmlFor="to">To (E.164)</Label>
                <Input id="to" placeholder="+15558675310" value={testTo} onChange={(e) => setTestTo(e.target.value)} />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="msg">Message</Label>
                <Textarea id="msg" rows={3} value={testMsg} onChange={(e) => setTestMsg(e.target.value)} maxLength={1600} />
              </div>
              <Button onClick={sendTest} disabled={sending || !testTo || !testMsg}>
                {sending && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
                Send SMS
              </Button>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2"><History className="h-5 w-5" />Recent API Calls</CardTitle>
              <CardDescription>Last 25 Twilio gateway calls.</CardDescription>
            </CardHeader>
            <CardContent>
              {logs.length === 0 ? (
                <p className="text-sm text-muted-foreground">No calls yet.</p>
              ) : (
                <div className="space-y-2">
                  {logs.map((log) => (
                    <div key={log.id} className="flex items-start justify-between border-b pb-2 text-sm">
                      <div>
                        <div className="font-medium">{log.action}</div>
                        <div className="text-xs text-muted-foreground">{new Date(log.created_at).toLocaleString()}</div>
                        {log.error_message && <div className="text-xs text-destructive mt-1">{log.error_message}</div>}
                      </div>
                      <Badge variant={log.status === 'success' ? 'default' : 'destructive'}>{log.status}</Badge>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
